import { useRecordContext, useT } from '@powerportalspro/react';
import {
  AggregateDataverseChartDataSource,
  QueryAggregate,
  DateGrouping,
  ChartType,
} from '@powerportalspro/react-charts';
import { DataverseChart } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// A bar chart of the won opportunities (statecode 1) of the record it sits in, summing their
// actual value by month and year of closing. `lookupColumn` is the opportunity column that
// points back at that record: `parentaccountid` on an account, `parentcontactid` on a contact.
// The record's id comes from the surrounding RecordContext; a new record has no opportunities
// yet, so the chart renders nothing until it is saved.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.ContactsAccounts.OpportunitiesWonByMonthChart';

export interface OpportunitiesWonByMonthChartProps {
  lookupColumn: string;
}

export function OpportunitiesWonByMonthChart({ lookupColumn }: OpportunitiesWonByMonthChartProps) {
  const t = useT();
  const { record } = useRecordContext();
  const recordId = record?.id;

  // Rebuilt only when the record or the lookup changes: the chart fetches again whenever it is
  // handed a different source.
  const source = useMemo(
    () =>
      recordId
        ? new AggregateDataverseChartDataSource({
            tableName: 'opportunity',
            groupByColumn: 'actualclosedate',
            groupByDateGrouping: DateGrouping.Month,
            aggregateColumn: 'actualvalue',
            aggregate: QueryAggregate.Sum,
            filterXml: `<filter>
              <condition attribute='statecode' operator='eq' value='1' />
              <condition attribute='${lookupColumn}' operator='eq' value='${recordId}' />
            </filter>`,
          })
        : null,
    [recordId, lookupColumn],
  );

  if (!source) return null;

  return (
    <DataverseChart
      dataSource={source}
      type={ChartType.Bar}
      title={t(`${STRINGS_BASE}.chart-title`)}
      height="320px"
      yAxisPrefix="$"
    />
  );
}
